import cn from 'classnames';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { Icon, IconNames } from '@/shared/ui';

import styles from './Popover.module.scss';

type PopoverProps = {
  children: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  popoverClassName?: string;
  iconClassName?: string;
  testID?: string;
  triggerIcon?: IconNames;
  triggerIconSize?: number;
  closeOnMouseLeave?: boolean;
};

export const Popover: FC<PopoverProps> = (props) => {
  const {
    children,
    isOpen: controlledIsOpen,
    onOpenChange,
    className,
    popoverClassName,
    iconClassName,
    testID,
    triggerIcon = 'dots',
    triggerIconSize = 32,
    closeOnMouseLeave = true,
  } = props;

  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (newValue: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(newValue);
    }
    onOpenChange?.(newValue);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        iconRef.current &&
        !iconRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTogglePopover = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleMouseLeave = () => {
    if (closeOnMouseLeave) {
      setIsOpen(false);
    }
  };

  return (
    <div className={cn(styles.container, className)} data-testid={testID}>
      <div className={styles.detailsIconWrapper} ref={iconRef}>
        <Icon
          name={triggerIcon}
          size={triggerIconSize}
          className={cn(
            styles.detailsIcon,
            isOpen && styles.detailsIconActive,
            iconClassName,
          )}
          onClick={handleTogglePopover}
        />
      </div>

      {isOpen && (
        <div
          className={cn(styles.popover, 'p2', popoverClassName)}
          onMouseLeave={handleMouseLeave}
          ref={popoverRef}
        >
          {children}
        </div>
      )}
    </div>
  );
};
